// DeviceID -> human label + which control screen to use. Mirrors the Flutter
// screenRedirector() mapping (docs/03-devices.md).

import { channelsFor } from './statusFormat';
import { panelColumns } from './buttonLayout';

export const ONE_TOUCH_IDS = [5, 6, 21, 22, 28, 31, 33, 40, 42, 55, 59, 60, 80];

// Combined switch + fan panels: DeviceID 92 = "4 + 1" (4 switches + 1 fan),
// DeviceID 93 = "7 + 1". Flutter routes both to switchFanScreen with a different
// switchCount — SWITCH_FAN_COUNT is that count.
export const SWITCH_FAN_COUNT = { 92: 4, 93: 7 };

export function deviceTypeKey(deviceId) {
  const id = Number(deviceId);
  if (id === 49) return 'fourTouch';
  if (id === 56) return 'twoTouch';
  if (id === 53) return 'fan';
  if (id === 51) return 'curtain';
  if (id === 24) return 'rgb';
  if (id === 25) return 'wwh';
  if (id === 23) return 'wwrgb';
  if (id === 3) return 'door';
  if (id === 82) return 'singlePhase';
  if (id === 16) return 'threePhase';
  if (id === 92 || id === 93) return 'switchFan';
  if (ONE_TOUCH_IDS.includes(id)) return 'oneTouch';
  return 'unsupported';
}

// How many relays a device exposes — drives status parsing and grid layout.
export function relayCount(deviceId) {
  const id = Number(deviceId);
  switch (deviceTypeKey(id)) {
    case 'fourTouch': return 4;
    case 'twoTouch': return 2;
    case 'oneTouch': return 1;
    case 'switchFan': return SWITCH_FAN_COUNT[id] || 4;
    default: return 0;
  }
}

// Grid column span per device. The 5-channel WWRGB light is wider so all its
// sliders fit; everything else uses the dense default.
/**
 * How wide a device's card needs to be.
 *
 * ⚠️ A PANEL IS AS WIDE AS ITS CHANNEL COUNT MAKES IT. This used to key on the
 * TYPE alone, so a 7+1 — eight channels, four panel columns — was given the
 * same quarter-row as a one-gang switch. Four labelled buttons cannot fit
 * ~55 px each, so they overflowed their grid tracks and drew on top of one
 * another: the overlap in the Bag room.
 *
 * The panel is two rows tall (buttonLayout.PANEL_ROWS), so the columns — not
 * the channels — are what set the width: 4 channels is 2 columns, 6 is 3, 8 is
 * 4. Growing with the columns keeps every button at a usable size instead of
 * compressing past the point of being readable.
 */
export function deviceGridSpan(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k === 'wwrgb') return { xs: 12, sm: 6, md: 6, lg: 4 };
  if (k === 'singlePhase' || k === 'threePhase') return { xs: 12, sm: 6, md: 6, lg: 4 };
  const cols = panelColumns(channelsFor(deviceId) || 1);
  if (cols >= 4) return { xs: 12, sm: 8, md: 6, lg: 4 };
  if (cols === 3) return { xs: 12, sm: 6, md: 4, lg: 3 };
  return { xs: 6, sm: 4, md: 3, lg: 2 };
}

/**
 * Fixed card width in px, for the horizontal strips that cannot use a grid.
 *
 * Same rule as deviceGridSpan and the same reason — a strip that hands every
 * card 230 px squeezes an eight-channel panel into four 50 px columns.
 */
export function deviceCardWidth(deviceId) {
  const k = deviceTypeKey(deviceId);
  if (k === 'singlePhase' || k === 'threePhase' || k === 'wwrgb') return 380;
  const cols = panelColumns(channelsFor(deviceId) || 1);
  // ~78 px a column plus the card's own padding: enough for the power icon and
  // a few characters of name without the label being pure ellipsis.
  return Math.max(230, 78 * cols + 40);
}

export function deviceLabel(deviceId) {
  switch (deviceTypeKey(deviceId)) {
    case 'fourTouch': return '4-Gang Switch';
    case 'twoTouch': return '2-Gang Switch';
    case 'oneTouch': return 'Switch';
    case 'fan': return 'Fan';
    case 'curtain': return 'Curtain';
    case 'rgb': return 'RGB Light';
    case 'wwh': return 'White Light';
    case 'wwrgb': return 'WWRGB Light';
    case 'door': return 'Door Lock';
    case 'singlePhase': return 'Energy Meter';
    case 'threePhase': return '3-Phase Meter';
    case 'switchFan': return `${SWITCH_FAN_COUNT[Number(deviceId)] || 4} + 1 Switch/Fan`;
    default: return 'Device';
  }
}
